import {Area, AreaAsync, IArea, IControlLabelContext, isArea} from "lib/commons/areas";
import {IO} from "lib/commons/io/io";
import {JLastDatas} from "lib/commons/lastDatas";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {IDepotResUiEnv, IDepotShortResUiEnv} from "lib/store/depot";
import {JSX} from "lib/commons/xml/dom";
import {IResInfosLineDrawer, OResInfoViewInit, ResInfoView} from "back/store/views/resInfosView";
import {LOCALE} from "lib/commons/lang";
import {OUserRefInit} from "back/core/widgets/userRef";
import {IUtBrowserCtx, OUtBrowserInit, UtBrowser, UtBrowserFetcher} from "back/store/views/utBrowser";
import {IView} from "lib/commons/views";
import {IJsTag, OResLayoutViewInit, ResLayoutView} from "back/store/views/resLayoutView";
import {OResAncViewInit, ResAncView} from "back/store/views/resAncView";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {OResAudioViewInit, OResChildrenViewInit, OResFrameViewInit, OResImgViewInit, OResVideoViewInit, ResAudioView, ResFrameView, ResImgView, ResVideoView} from "back/store/views/resContentView";
import {InfoFocusRes, IResFormCtx, IResFormIdentCtx, JCidMetas} from "lib/store/res";
import {OResVersionsViewInit, ResVersionsView} from "back/store/views/resVersionsView";
import {AccelKeyMgr, IAction} from "lib/commons/actions";
import {ResBodyView} from "back/store/views/resViewer";
import {TemplateResult} from "res/litHtml/lib/template-result";
import {render} from "res/litHtml/lit-html";
import {FORMS, IField} from "lib/commons/forms";
import {OResIdentFieldSetInit, ResIdentFieldSet} from "back/store/widgets/resInputs";
import {OTabsInit, Tabs} from "back/commons/widgets/tabs";
import {OResTemplatesViewInit, ResTemplatesView} from "back/store/views/resTemplatesView";
import "back/store/areas/areas_Perms";


/**
 * Transforme une Area pour êtr exploité dans un autre contexte.
 */
export interface IResAreaAdapter {
	/** Transforme cette area (issu de la view "main") pour un usage de type sélection de ressource (view "select"). */
	asSelectArea?(): IArea<IRegPointer<IDepotResUiEnv>>

	/** */
	asConsultArea?(): IArea<IRegPointer<IDepotResUiEnv>>
}

/**
 * Area construisant un ensemble d'autres views d'une ressource.
 */
export class ResLayoutArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, ResLayoutView> {

	scroll?: 'small' | 'large'

	body: (IArea<IRegPointer<IDepotResUiEnv>, IView> | IJsTag | string)[]

	constructor(id?: string) {
		super(id || 'resLayout');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resLayoutView.js"));
	}

	setBody(...body: (IArea<IRegPointer<IDepotResUiEnv>, IView> | IJsTag | string)[]): this {
		this.body = body;
		return this;
	}

	setScroll(scroll: 'small' | 'large' | null): this {
		this.scroll = scroll;
		return this;
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResLayoutView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResLayoutViewInit = {
			area: this,
			areaContext: ctx,
			reg: ctx.reg,
			lastDatas: lastDatas,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			body: this.body,
			scroll: this.scroll
		};
		return <store-res-layout î={init}/> as ResLayoutView;
	}
}

/**
 * Area construisant la view des infos d'une ressource.
 */
export class ResInfoArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, ResInfoView> {

	lines: IResInfosLineDrawer[]

	constructor() {
		super('resInfo');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resInfosView.js"));
	}

	setLines(...lines: IResInfosLineDrawer[]): this {
		this.lines = lines;
		return this;
	}

	addDefaultLines(): this {
		if (!this.lines) this.lines = [];
		this.lines.push(RESINFO.path, RESINFO.url, RESINFO.status, RESINFO.deploy, RESINFO.prc); /*, RESINFO.debug , RESINFO.moved A REVOIR : si en tooltip, fermer le tooltip container ? */
		return this;
	}

	addLines(...lines: IResInfosLineDrawer[]): this {
		if (!this.lines) this.lines = [];
		if (lines) this.lines.push(...lines);
		return this;
	}


	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResInfoView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResInfoViewInit = {
			area: this,
			areaContext: ctx,
			reg: ctx.reg,
			lastDatas: lastDatas,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			lines: this.lines
		};
		return <store-res-infos î={init}/> as ResInfoView;
	}
}


/**
 * Area construisant la view des ancêtres d'une ressource.
 */
export class ResAncArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, ResAncView> {

	constructor() {
		super('resAnc');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resAncView.js"));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResAncView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResAncViewInit = {
			area: this,
			areaContext: ctx,
			reg: ctx.reg,
			lastDatas: lastDatas,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx)
		};
		return <store-res-anc î={init}/> as ResAncView;
	}
}


/**
 * Area construisant la view des versions d'une ressource.
 */
export class ResVersionsArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, ResVersionsView> {

	constructor() {
		super('resVersions');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resVersionsView.js"));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResVersionsView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResVersionsViewInit = {
			reg: ctx.reg,
			// lastDatas: lastDatas,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx)
		};
		return <store-res-versions î={init}/> as ResVersionsView;
	}
}


/**
 * Area pour les noeuds de type folder affichant la liste des noeuds fils.
 */
export class ResChildrenArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, HTMLElement> implements IResAreaAdapter {

	actions?: IAction<IRegPointer<IDepotResUiEnv>>[];

	noEdit?: 'strict' | 'addFolder';

	/**
	 * Filre pour lister les fils et la recherche.
	 * Exemples :
	 * - "isFolder"
	 * - "isFolder & !inTrash & !unlisted &!moved"
	 * - "usable" ( = !inTrash & !unlisted &!moved)
	 * - "regexpPrc(img.*) & !inTrash"
	 * - "prc(image,audio,video) & !inTrash"
	 * - "" : pas de filtre mais forcera le mode un seul dossier virtuel lors du listing des fils via le utBrowser.
	 * - null : pas de filtre
	 *
	 * @see eu.scenari.urltree.util.UrlTreeNodePredicates
	 */
	filter?: string

	get depotActions(): typeof import('back/store/actions/depotActions') {return this._modules[0]}

	get resActions(): typeof import('back/store/actions/resActions') {return this._modules[1]}

	constructor(id?: string) {
		super(id || 'resChildren');
		this.iniLibs();
	}

	iniLibs() {
		this.requireLib(
			IO.asEndPoint("/@back@/store/actions/depotActions.js"),
			IO.asEndPoint("/@back@/store/actions/resActions.js"),
			IO.asEndPoint("/@back@/store/views/resContentView.js")
		);
	}

	asSelectArea(): IArea<IRegPointer<IDepotResUiEnv>> {
		if (!this.asSelect) {
			this.asSelect = Object.create(this);
			this.asSelect.noEdit = 'addFolder';
			this.asSelect.actions = null;
		}
		return this.asSelect;
	}

	asSelect: ResChildrenArea;

	asConsultArea(): IArea<IRegPointer<IDepotResUiEnv>> {
		if (!this.asConsult) {
			this.asConsult = Object.create(this);
			this.asConsult.noEdit = 'strict';
			this.asConsult.actions = null;
		}
		return this.asConsult;
	}

	asConsult: ResChildrenArea;

	/** Retourne l'area pour construire la vue fondée sur utBrowser lorsque le dossier contient trop de fils. */
	asResTreeArea(): ResTreeArea {
		const area = new ResTreeArea();
		area.noEdit = this.noEdit;
		area.actions = this.actions;
		return area;
	}

	async loadBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): Promise<HTMLElement> {
		if (!(this instanceof ResTreeArea)) {
			const showMode = ctx.reg.getSvc<IResChildrenShowModeSvc>("storeResChildrenShowMode");
			if (typeof showMode === "function" && showMode() === 'tree') {
				return this.asResTreeArea().loadBody(ctx, lastDatas);
			}
		}
		return super.loadBody(ctx, lastDatas);
	}

	/** Force à true pour toujours passer par le switch dynamique IResChildrenShowModeSvc. */
	needAsync(ctx: IRegPointer<IDepotResUiEnv>): boolean {return true}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): HTMLElement {
		if (ctx.reg.isClosed) return null; //raceCond
		//actionsStart
		return <store-res-children î={{
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			filter: this.filter,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			actionsBar: this.getActionsBar(ctx)
		} as OResChildrenViewInit}/>;
	}

	protected getActionsBar(ctx: IRegPointer<IDepotResUiEnv>): OBarActionsInit<IRegPointer<IDepotResUiEnv>> | null {
		let actions = this.actions;
		if (actions === undefined) {
			if (this.noEdit === "strict") return null;
			actions = [];
			if (this.noEdit !== "addFolder") actions.push(new this.resActions.AddByImport());
			actions.push(new this.resActions.AddFolder());
		}
		return actions ? {
			reg: ctx.reg,
			actions,
			actionContext: ctx,
			uiContext: "dialog",
			disableFullOverlay: true
		} : null;
	}
}

/**
 * Svc "storeResChildrenShowMode" permettant de forcer à l'init le choix entre ResChildrenArea et ResTreeArea.
 */
export type IResChildrenShowModeSvc = () => 'children' | 'tree'


/**
 * Area pour les noeuds de type folder affichant le sous-arbre des descendants.
 */
export class ResTreeArea extends ResChildrenArea {

	constructor() {super('resTree');}

	iniLibs() {
		this.requireLib(
			IO.asEndPoint("/@back@/store/actions/depotActions.js"),
			IO.asEndPoint("/@back@/store/actions/resActions.js"),
			IO.asEndPoint("/@back@/store/views/utBrowser.js")
		);
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): UtBrowser {
		if (ctx.reg.isClosed) return null; //raceCond
		//actionsStart
		const init: OUtBrowserInit = {
			reg: ctx.reg,
			lastDatas: lastDatas?.ch,
			lastDatasKey: "ch",
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			pathRoot: ctx.reg.env.nodeInfos.permaPath,
			fetcher: new UtBrowserFetcher(ctx.reg.env.universe.adminUrlTree).setFilter(this.filter),
			infoBroker: ctx.reg.env.infoBroker,
			defaultAction: new this.depotActions.FocusLiveRes(),
			actionsStart: this.getActionsBar(ctx),
			grid: {skinScroll: "scroll/large"},
			actions: ctx.reg.mergeLists("actions:store:ResTreeView", "actions:store:resList"),
			accelKeyMgr: new AccelKeyMgr<IUtBrowserCtx>().initFromMapActions(ctx.reg.mergeListsAsMap("accelkeys:store:ResTreeView", "accelkeys:store:resList"))
		};
		if (this.noEdit) init.resHandlingMode = "sel";
		return <store-ut-browser î={init}/> as UtBrowser;
	}
}

/**
 * Onglets pour multi-vues du contenu de la res.
 * Si une seule view est effectivement visible (restriction via perms), le widget des onglets est éliminé.
 * Peut donc aussi être utilisé pour offrir un switch entre différentes vues en fonction des perms des users.
 */
export class ResTabsArea extends Area<IRegPointer<IDepotResUiEnv>, HTMLElement> {

	tabs: IArea<IRegPointer<IDepotResUiEnv>, IView>[]

	constructor(id?: string) {
		super(id || 'resTabs');
	}

	setTabs(...tabs: IArea<IRegPointer<IDepotResUiEnv>, IView>[]): this {
		this.tabs = tabs;
		return this;
	}

	needAsync(ctx: IRegPointer<IDepotResUiEnv>): boolean {
		return this.tabs.some(t => t.needAsync(ctx));
	}

	async loadLibs(ctx: IRegPointer<IDepotResUiEnv>): Promise<void> {
		await Promise.all(this.tabs.filter(t => t.needAsync(ctx)).map(t => t.loadLibs(ctx)));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): HTMLElement {
		const tabs = this.tabs.filter((t) => t.isVisible(ctx));
		if (tabs.length > 1) {
			return <Tabs class="noBorder" î={{
				reg: ctx.reg,
				areas: tabs,
				areasContext: ctx,
				skinOver: "store-res-bodytabs"
				//lastDatas
			} as OTabsInit<IRegPointer<IDepotResUiEnv>>}/>;
		} else if (tabs.length === 1) {
			return tabs[0].buildBody(ctx, lastDatas);
		}
		return null;
	}
}


REG.reg.registerSkin("store-res-bodytabs", 1, /* language=CSS */ `
	:host {
		flex: 1;
	}

	c-tab {
		flex: 1;
		border-width: 3px !important;
		padding: .5em !important;
		font-weight: bold !important;
		color: var(--fade-color);
	}
`);


abstract class ResContentArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, HTMLElement> {
	actionLists: string[]

	setActionsLists(actionLists: string[]): this {
		this.actionLists = actionLists;
		return this;
	}

	protected getActionsBar(ctx: IRegPointer<IDepotResUiEnv>): OBarActionsInit<IRegPointer<IDepotResUiEnv>> | null {
		return this.actionLists ? {
			reg: ctx.reg,
			actions: ctx.reg.mergeLists(...this.actionLists),
			actionContext: ctx,
			uiContext: "dialog",
			disableFullOverlay: true
		} : null;
	}
}

/**
 * Area pour afficher une res sans contenu
 */
export class ResNoContentArea extends ResContentArea {

	constructor() {
		super('resNoContent');
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): BarActions<IRegPointer<IDepotResUiEnv>> {
		if (ctx.reg.isClosed) return null; //raceCond
		const bar = <BarActions id={this.getId()}
														î={this.getActionsBar(ctx)}/> as BarActions<IRegPointer<IDepotResUiEnv>>;
		return bar;
	}
}

/**
 * Area pour afficher le contenu d'une res via une iframe.
 */
export class ResFrameArea extends ResContentArea {

	constructor(public subPath?: string, public treeSrv?: 'adminUrlTree' | 'urlTree') {
		super('resFrame');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resContentView.js"));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResFrameView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResFrameViewInit = {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			// lastDatas: lastDatas?.ch,
			// lastDatasKey: "ch",
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			subPath: this.subPath,
			treeSrv: ctx.reg.env.universe[this.treeSrv || 'urlTree'],
			actionsBar: this.getActionsBar(ctx)
		};
		return <store-res-frame î={init}/> as ResFrameView;
	}
}

/**
 * Area pour afficher le contenu d'une res via une img.
 */
export class ResImgArea extends ResContentArea {

	checkboard: boolean;

	constructor(public subPath: string, public treeSrv?: 'adminUrlTree' | 'urlTree') {
		super('resImg');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resContentView.js"));
	}

	showCheckboard(show: boolean): this {
		this.checkboard = show;
		return this;
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResImgView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResImgViewInit = {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			// lastDatas: lastDatas?.ch,
			// lastDatasKey: "ch",
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			subPath: this.subPath,
			treeSrv: ctx.reg.env.universe[this.treeSrv || 'urlTree'],
			checkboard: this.checkboard,
			actionsBar: this.getActionsBar(ctx)
		};
		return <store-res-img î={init}/> as ResImgView;
	}
}

/**
 * Area pour afficher le contenu d'une res audio.
 */
export class ResAudioArea extends ResContentArea {

	checkboard: boolean;

	constructor(public subPath: string, public treeSrv?: 'adminUrlTree' | 'urlTree') {
		super('resAudio');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resContentView.js"));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResAudioView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResAudioViewInit = {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			// lastDatas: lastDatas?.ch,
			// lastDatasKey: "ch",
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			subPath: this.subPath,
			treeSrv: ctx.reg.env.universe[this.treeSrv || 'urlTree'],
			actionsBar: this.getActionsBar(ctx)
		};
		return <store-res-audio î={init}/> as ResAudioView;
	}
}

/**
 * Area pour afficher le contenu d'une res vidéo
 */
export class ResVideoArea extends ResContentArea {

	constructor(public subPath: string, public treeSrv?: 'adminUrlTree' | 'urlTree') {
		super('resAudio');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resContentView.js"));
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResVideoView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResVideoViewInit = {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			// lastDatas: lastDatas?.ch,
			// lastDatasKey: "ch",
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			subPath: this.subPath,
			treeSrv: ctx.reg.env.universe[this.treeSrv || 'urlTree'],
			actionsBar: this.getActionsBar(ctx)
		};
		return <store-res-video î={init}/> as ResVideoView;
	}
}

export class ResBarActionsArea extends Area<IReg<IDepotResUiEnv>, BarActions<IRegPointer<IDepotResUiEnv>>> {

	constructor(public actionLists: string[], id?: string) {
		super(id || "resBarActions");
	}

	buildBody(ctx: IReg<IDepotResUiEnv>, lastDatas?: JLastDatas): BarActions<IRegPointer<IDepotResUiEnv>> {
		const bar = <BarActions id={this.getId()}
														î={{reg: ctx, actions: ctx.mergeLists(...this.actionLists), actionContext: ctx, disableFullOverlay: true} as OBarActionsInit<IRegPointer<IDepotResUiEnv>>}/> as BarActions<IRegPointer<IDepotResUiEnv>>;
		ctx.env.nodeInfosChange?.on("histChange", () => {bar.refreshContent()});
		return bar;
	}
}


/**
 * Area construisant la main view standard.
 */
export class ResMainArea extends ResLayoutArea implements IResAreaAdapter {

	contentViews: (IArea<IRegPointer<IDepotResUiEnv>, IView> | IJsTag | string)[]

	static ancArea = new ResAncArea();
	static actionsArea = new ResBarActionsArea(["actions:depot:resMainView:edit"], "resEdit");
	static headerTag = {tag: 'header', children: [ResMainArea.ancArea, ResMainArea.actionsArea]};
	static headerSelectTag = {tag: 'header', children: [ResMainArea.ancArea]};

	/** header standard dans le views main, select...*/
	static titleLine: IResInfosLineDrawer = {
		drawLine(reg: IReg<IDepotResUiEnv>, parent: Node) {
			const ni = reg.env.nodeInfos;
			const resType = reg.env.resType;
			const actions = <c-bar-actions î={{reg, actionContext: reg, actions: reg.getList("actions:depot:resMainView:infos")} as OBarActionsInit<IRegPointer<IDepotResUiEnv>>}/> as BarActions<IRegPointer<IDepotResUiEnv>>;
			const versions = (ni.t !== "moved" ? resType.prcVersionning === "VCS" : ni.hist || ni.succ) && reg.getPref("depot.showVersions", true);
			//console.log("resType.prcVersionning:::",resType.prcVersionning);
			const name = <span class="name">{resType.resName(ni)}</span>;
			const versionInTi = !versions && resType.prcVersionning === "VCB" ? <span id="versionInTi">@<ResVersionsView î={{reg, showDate: false, onlyChangedContent: true} as OResVersionsViewInit}/></span> : undefined;
			let flagInTi: Element | undefined;
			if (ni.trashed) {
				flagInTi = <span class="trashed" title={reg.env.resTypes.visStateDescription(ni)}/>
			} else	if (ni.unlisted) {
				flagInTi = <span class="unlisted" title={reg.env.resTypes.visStateDescription(ni)}/>
			}
			parent.appendChild(<div id="title">
				<img class="icon" src={resType.resIcon(ni)}/>
				<span id="nameCtn">{name}{versionInTi}{flagInTi}</span>
				{versions ? undefined : actions}
			</div>);
			if (versions) {
				parent.appendChild(<div id="version">
					<ResVersionsView î={{reg, showDate: true, onlyChangedContent: true} as OResVersionsViewInit}/>
					{actions}
				</div>);
			}
			const infosArea = resType.resView("infosMain");
			if (infosArea) {
				function addInfosMain(view: HTMLElement) {
					if (!view) return;
					view.id = "infosMain";
					reg.installSkin("store-res-infosMain", view.shadowRoot);
					parent.appendChild(view);
				}

				if (infosArea.needAsync(reg)) {
					infosArea.loadBody(reg).then(addInfosMain);
				} else {
					addInfosMain(infosArea.buildBody(reg));
				}
			}
		}
	}
	static titleArea = new ResInfoArea().setSkin("store-res-titlebar").setLines(ResMainArea.titleLine);

	constructor() {
		super('resMain');
		this._skinOver = "store-res-main";
		this.scroll = "large";
		this.requireLib(IO.asEndPoint("/@back@/store/views/resMainView.js"));
	}

	setContent(...contentView: (IArea<IRegPointer<IDepotResUiEnv>, IView> | IJsTag | string)[]): this {
		this.contentViews = contentView;
		this.body = [
			ResMainArea.headerTag,
			{tag: 'main', children: [ResMainArea.titleArea, ...contentView]}
		]
		return this;
	}

	asSelectArea(): IArea<IRegPointer<IDepotResUiEnv>> {
		if (!this._selectArea) {
			//suppr le ResBarActionsArea ?
			this._selectArea = new ResMainArea();
			this._selectArea.contentViews = this.contentViews.map((cv) => (cv as IResAreaAdapter)?.asSelectArea ? (cv as IResAreaAdapter).asSelectArea() : cv);
			this._selectArea.body = [
				ResMainArea.headerSelectTag,
				{tag: 'main', children: [ResMainArea.titleArea, ...this._selectArea.contentViews]}
			]
		}
		return this._selectArea;
	}

	_selectArea: ResMainArea

	asConsultArea(): IArea<IRegPointer<IDepotResUiEnv>> {
		if (!this._consultArea) {
			//suppr le ResBarActionsArea ?
			this._consultArea = new ResMainArea();
			this._consultArea.contentViews = this.contentViews.map((cv) => (cv as IResAreaAdapter)?.asConsultArea ? (cv as IResAreaAdapter).asConsultArea() : cv);
			this._consultArea.body = [
				{tag: 'main', children: [ResMainArea.titleArea, ...this._consultArea.contentViews]}
			]
		}
		return this._consultArea;
	}

	_consultArea: ResMainArea
}


REG.reg.registerSkin('store-res-infosMain', 1, /* language=CSS */ `
	:host {
		padding: 0 0 .5em 0;
	}

	.label, .lbl {
		justify-content: flex-start;
		font-variant: all-small-caps;
		color: var(--fade-color);
	}
`);


/**
 * Area construisant le tooltip standard.
 */
export class ResHoverArea extends ResLayoutArea {
	constructor() {
		super('resHover');
		this._skinOver = "store-res-hover";
	}

	setPreview(...preview: (IArea<IReg<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string)[]): this {
		this.body = [
			new ResInfoArea().setLines(RESINFO.path, RESINFO.url, RESINFO.status, RESINFO.prc),
			...preview
		]
		return this;
	}
}

REG.reg.registerSkin('store-res-hover', 1, /* language=CSS */ `
	:host {
		max-width: 25rem;
		min-width: 20rem;
	}
`);


/**
 * Area construisant un fieldSet d'édition des metas.
 */
export class ResFormMetasArea extends AreaAsync<IResFormCtx, HTMLFieldSetElement> {

	body: (IArea<IResFormCtx, IView> | IJsTag | string)[]

	constructor(id?: string) {
		super(id || 'formMetas');
		this.requireLib(IO.asEndPoint("/@back@/store/views/resLayoutView.js"));
	}

	setBody(...body: (IArea<IResFormCtx, IView> | IJsTag | string)[]): this {
		this.body = body;
		return this;
	}

	async loadLibs(ctx: IResFormCtx): Promise<void> {
		await super.loadLibs(ctx);
		const promises: Promise<any>[] = [];
		ResLayoutView.loadLibs(this.body, ctx, promises);
		return Promise.all(promises) as Promise<any>;
	}

	buildBody(ctx: IResFormCtx, lastDatas?: JLastDatas): HTMLFieldSetElement {
		if (ctx.reg.isClosed) return null; //raceCond
		const fieldset = document.createElement("fieldset") as HTMLFieldSetElement;
		for (const v of this.body) {
			ResLayoutView.buildSrcView(v, fieldset, ctx, null);
		}
		return fieldset;
	}
}


/**
 * Area construisant l'écran de gestion des gabarits (accueil dynamiques, 404 not found...).
 */
export class ResTemplatesArea extends AreaAsync<IRegPointer<IDepotResUiEnv>, ResTemplatesView> {

	config?: OResTemplatesViewInit;

	constructor(id?: string) {
		super(id || 'resTemplates');
		this._label = "Gabarits";
		this.requireLib(IO.asEndPoint("/@back@/store/views/resTemplatesView.js"));
		this.requireVisiblePerm("ui.store.area.resTemplates");
	}

	setTemplatesConfig(config: OResTemplatesViewInit): this {
		this.config = config;
		return this;
	}

	buildBody(ctx: IRegPointer<IDepotResUiEnv>, lastDatas?: JLastDatas): ResTemplatesView {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResTemplatesViewInit = Object.assign({
			area: this,
			areaContext: ctx,
			reg: ctx.reg,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx)
		}, this.config);
		return <store-res-templates î={init}/> as ResTemplatesView;
	}
}


/** Constructeurs des lignes des resInfo. */
export namespace RESINFO {

	export const path: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			parent.appendChild(<div class="line"><span class="label">Chemin</span><span class="value">{ctx.reg.env.nodeInfos.permaPath || "/"}</span></div>);
		}
	}

	export const url: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const url = (ctx.reg.env as IDepotResUiEnv).nodeInfos.publicUrl;
			if (url) parent.appendChild(<div class="line"><span class="label">URL</span><a class="value" href={url} target="_blank">{url}</a></div>);
		}
	}

	export const deploy: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const metas = (ctx.reg.env as IDepotResUiEnv).nodeInfos.metas;
			if (!metas) return;
			const dt = ctx.reg.env.nodeInfos.m || metas.deployDate; //ctx.reg.env.nodeInfos.m pour cohérence (à la seconde près) avec affichage des versions
			if (!dt) return;
			const by = metas.deployedBy;
			parent.appendChild(<div class="line"><span class="label">Modification</span><span class="value">
				{LOCALE.formatDateDigitsToSec.format(new Date(dt))}
				{by ? <span> par <c-userref class="inline" î={{reg: ctx.reg, nickOrAccount: by} as OUserRefInit}/></span> : undefined}
			</span></div>);
		}
	}

	export const status: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const ni = (ctx.reg.env as IDepotResUiEnv).nodeInfos;
			ctx.reg.env.resTypes.visStateMenuLabel
			if (ni.trashed || ni.unlisted) {
				parent.appendChild(<div class="line"><span class="label">{ctx.reg.env.resTypes.visStateMenuLabel}</span><span class="value" title={ctx.reg.env.resTypes.visStateDescription(ni)}>
					<img style="height:.7em; vertical-align:center; margin-inline-end:.3em" src={ni.trashed ? "/@skin@/store/states/invisible.svg" : "/@skin@/store/states/limited.svg"}/>
					{ctx.reg.env.resTypes.visStateLabel(ni)}
				</span></div>);
			}
		}
	}

	export const prc: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			parent.appendChild(<div class="line"><span class="label">Traitement</span><span class="value">{ctx.reg.env.resType.prcLabel}</span></div>);
		}
	}

	export const moved: IResInfosLineDrawer = {
		drawLine(ctx: IRegPointer<IDepotResUiEnv>, parent: Node, view: ResBodyView) {
			const movedPath = ctx.reg.env.nodeInfos.movedPath;
			if (movedPath) {
				parent.appendChild(<div class="line"><span class="label">Déplacé vers</span><a href="javascript:void(0)" class="value" onclick={(ev: Event) => {
					ctx.reg.env.infoBroker?.dispatchInfo(new InfoFocusRes(movedPath), view);
				}}>{movedPath}</a></div>);
			}
		}
	}

	export const debug: IResInfosLineDrawer = {
		async drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			parent.appendChild(<div class="line"><span class="label">nodeInfos:::</span><span class="value">{JSON.stringify(ctx.reg.env.nodeInfos)}</span></div>);
			const render = <span class="value">loading...</span>;
			parent.appendChild(<div class="line"><span class="label">renderings:::</span>{render}</div>);
			render.textContent = JSON.stringify((await ctx.reg.env.universe.getRenderings(ctx.reg)).datas);
		}
	}

	export class SimpleMeta implements IResInfosLineDrawer {

		constructor(public metaLabel: string, public metaName: string) {}

		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const metas = (ctx.reg.env as IDepotResUiEnv).nodeInfos.metas;
			if (metas && this.metaName in metas) parent.appendChild(<div class="line"><span class="label">{this.metaLabel}</span><span class="value">{metas[this.metaName]}</span></div>);
		}
	}

	/**
	 * Classe de base déclarant une ligne
	 */
	export class BasicResLine implements IResInfosLineDrawer {

		protected label: string | TemplateResult | Node | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => Node)

		protected values: (string | TemplateResult | Node | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => Node) | IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView>)[]

		protected visible: boolean | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => boolean)

		setLabel(label: string | TemplateResult | Node | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => Node)): this {
			this.label = label;
			return this;
		}

		setValues(...values: (string | TemplateResult | Node | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => Node) | IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView>)[]): this {
			this.values = values;
			return this;
		}

		appendValues(...values: (string | TemplateResult | Node | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => Node) | IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView>)[]): this {
			if (!this.values) this.values = [];
			if (values) this.values.push(...values);
			return this;
		}

		isVisible(value: boolean | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => boolean)): this {
			this.visible = value;
			return this;
		}

		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const visible = typeof this.visible === 'function' ? this.visible(ctx) : this.visible;
			if (visible !== false) {
				let label = typeof this.label === 'function' ? this.label(ctx) : this.label;
				let labelCtn;
				if (label instanceof TemplateResult) {
					labelCtn = <span class="value"/>;
					render(label, labelCtn);
				} else
					labelCtn = <span class="label">{label}</span>;

				let valuesCtn = <span class="value"/>;
				if (this.values) {
					this.values.forEach((value) => {
						const valueEntry = valuesCtn.appendChild(<span class="valueEntry"/>);
						if (typeof value === 'function')
							value = value(ctx);
						if (value instanceof TemplateResult) {
							render(value, valueEntry);
						} else if (isArea(value)) {
							valueEntry.appendChild(value.buildBody(ctx, view));
						} else if (typeof value === 'string')
							valueEntry.appendChild(document.createTextNode(value));
						else
							throw "unknown type : " + typeof value
					})
				}
				parent.appendChild(<div class="line">{labelCtn}{valuesCtn}</div>);
			}
		}
	}

	/**
	 * Classe de base déclarant une ligne à partir d'une area "ResForm"
	 */
	export class ResFormAreaLine implements IResInfosLineDrawer {

		constructor(public metaName: string, protected area?: IArea<IResFormCtx, IView>, protected onlyIfHasContent = false) { }

		setArea(area: IArea<IResFormCtx, IView>): this {
			this.area = area;
			return this;
		}

		drawLine(ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node, view: ResBodyView) {
			const metas = (ctx.reg.env as IDepotResUiEnv).nodeInfos.metas;
			if (!this.onlyIfHasContent || (metas && this.metaName in metas)) {
				const elt = parent.appendChild(this.area.buildBody(Object.assign({buildControlLabel: true} as IControlLabelContext, ctx), null));
				FORMS.jsonToForm(metas || {}, elt);
			}
		}

	}

	// PB: load lib...
	// export class ResPreviewLine implements IResInfosLineDrawer {
	//
	// 	constructor(public tag: 'store-res-img' | 'store-res-frame' | string, public init: OResImgViewInit | OResFrameViewInit | any) {}
	//
	// 	drawLine(reg: IReg<IDepotResUiEnv | IDepotShortResUiEnv>, parent: Node) {
	// 		parent.appendChild(<div class="lineMerged">{(document.createElement(this.tag) as IEltInitable).initialize(this.init)}</div>);
	// 	}
	// }
}


/**
 * Area construisant les champs d'édition des metas identificatoires d'un res.
 */
export class ResIdentFieldSetArea extends AreaAsync<IResFormIdentCtx, IField & HTMLElement> {

	/** Affiche le widget standard de sélection du dossier parent. Si true, pathBuilder est obligatoire. */
	noFolderSelector?: boolean

	/** Affiche le widget standard de saisie du nom de la ressource. Si true, pathBuilder est obligatoire. */
	noResNameField?: boolean

	/** Areas de construction des champs identificatoires customs. */
	customInputs?: IArea<IResFormIdentCtx>[];

	/**
	 * Fonction de construction du path (versionné si VCB).
	 * Ce path construit doit être affecté dans metasInOut.path.
	 *
	 * Cette fonction est appelée après l'appel à FORMS.formToJson() sur chaque champs construits par les customInputs
	 * et a complété ainsi metasInOut.
	 *
	 * resIdentField permet d'obtenir le dossier parent et le nom de la res si les widgets standards sont utilisés.
	 */
	pathBuilder?: (metasInOut: JCidMetas, resIdentField: ResIdentFieldSet) => void

	constructor() {
		super('ResIdentFieldSet');
		this.requireLib(IO.asEndPoint("/@back@/store/widgets/resInputs.js"));
	}

	/** Configuration custom des metas identificatoires. */
	setCustomFields(customInputs: IArea<IResFormIdentCtx>[],
									pathBuilder: (metasInOut: JCidMetas, resIdentFields: ResIdentFieldSet) => void,
									noFolderSelector?: boolean,
									noResNameField?: boolean): this {
		this.customInputs = customInputs;
		this.pathBuilder = pathBuilder;
		this.noFolderSelector = noFolderSelector;
		this.noResNameField = noResNameField;
		return this;
	}

	buildBody(ctx: IResFormIdentCtx): ResIdentFieldSet {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OResIdentFieldSetInit = Object.create(ctx);
		init.area = this;
		init.skin = this.getSkin(ctx);
		init.skinOver = this.getSkinOver(ctx);
		return <store-res-ident-fieldset î={init}/> as ResIdentFieldSet;
	}
}